import { defineLocalStore } from '@/utils/localStore'
import { computed, reactive, readonly, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { handleAsync, confirm, setSnackbar } from 'best-modules/plugins'
import {
  documentDelete,
  documentRejectRegistration,
  documentReturnFromRegistration,
  documentSendToRegistration,
  documentSingle,
  documentUpdate,
  documentReviewList,
} from '@/requests/document'
import { cachedObject } from 'best-modules/utils'
import { DocumentDTO, Document } from '@/utils/types/document'
import { useDocumentRoute } from './composables/documentRoute'
import { useDocumentFlow } from './composables/documentFlow'
import { useAcceptance } from './composables/acceptance'
import { useAttachments } from './composables/attachments'
import { useRelation } from './composables/relation'
import { useVersions } from './composables/versions'
import { useResolutions } from './composables/resolutions'
import { DocumentContext } from './types'
import { pick } from 'lodash'
import { useCan } from '@/composables/useCan'
import { openDialog } from '@/dialog'
import { useBreadcrumb, useStore } from '@/plugins'

export const { initLocalStore: initDocument, useLocalStore: useDocument } =
  defineLocalStore('document', (payload?: { disabled: boolean }) => {
    const route = useRoute()
    const router = useRouter()
    const { can } = useCan()
    const store = useStore()
    const userId = computed(() => store.state.user.userData?.id)
    const breadcrumb = useBreadcrumb()

    const documentUuid = computed<string>(() => {
      return String(route.params.uuid || route.params.docUuid || '')
    })
    const documentId = computed<number>(() => documentSource.id ?? 0)
    const documentRequestError = ref()
    const documentSource = reactive<Document>({} as Document)
    const documentCredentials = cachedObject<DocumentDTO>({
      registrationJournalId: null,
      registrationTypeId: null,
      accessLevelTypeId: null,
      registrationNumber: null,
      registrationDate: null,
      description: null,
      document: null,
      fieldValues: null,
      executorId: null,
      status: null,
    } as unknown as DocumentDTO)
    documentCredentials.$setIgnore('document')

    const updateDocumentSource = (obj: Document) => {
      Object.assign(documentSource, obj)
    }

    const setDocument = (obj: Document) => {
      Object.assign(documentSource, obj)
      const credentials = pick(obj, [
        'registrationJournalId',
        'registrationTypeId',
        'accessLevelTypeId',
        'registrationNumber',
        'registrationDate',
        'description',
        'document',
        'fieldValues',
        'registrationTypeId',
        'executorId',
        'status',
      ])
      credentials.fieldValues = credentials.fieldValues.map(f => {
        return {
          fieldDefinitionId: f.fieldDefinitionId,
          value: f.value?.value ?? null,
        }
      })
      documentCredentials.$set(credentials)
    }

    const getDocument = () => {
      return handleAsync('getDocument', async () => {
        try {
          const data = await documentSingle(documentUuid.value)
          setDocument(data)

          if (data.uuid && documentUuid.value !== data.uuid) {
            router.replace({ params: { uuid: data.uuid } })
          }
        } catch (e) {
          documentRequestError.value = e
        }
      })
    }

    const disabled = computed<boolean>(() => {
      if (payload?.disabled) return true
      return (
        documentSource.createdBy !== userId.value ||
        !['draft', 'revision'].includes(documentSource.status) ||
        !can('documents.update')
      )
    })

    const documentContext: DocumentContext = {
      documentId,
      setDocument,
      getDocument,
      updateDocumentSource,
      documentSource: readonly(documentSource),
      disabled,
    }

    const updateDocument = () => {
      return documentUpdate(documentId.value, documentCredentials.$object).then(
        res => {
          setDocument(res)
          setSnackbar({ text: 'Документ успішно оновлено' })
        }
      )
    }

    const updateDocumentFile = () => {
      return documentUpdate(documentId.value, {
        ...documentCredentials.$cache,
        document: documentCredentials.document,
      }).then(res => {
        documentSource.document = res.document
        documentCredentials.document = res.document
        setSnackbar({ text: 'Документ успішно оновлено' })
      })
    }

    const revision = () => {
      openDialog({
        name: 'DocumentRevision',
        dialogData: {
          documentId: documentId.value,
        },
        params: {
          cb(res: Document) {
            setDocument(res)
            setSnackbar({ text: 'Документ повернуто на доопрацювання' })
          },
        },
      })
    }

    const sendToRegistration = () => {
      return handleAsync('documentSendToRegistration', async () => {
        const res = await documentSendToRegistration(documentId.value)
        setDocument(res)
        setSnackbar({ text: 'Документ успішно відправлено на реєстрацію' })
      })
    }

    const registerDocument = () => {
      openDialog({
        name: 'DocumentRegistration',
        dialogData: {
          document: documentSource,
        },
        params: {
          submitLabel: 'Зареєструвати',
          cb: (res: Document) => {
            setDocument(res)
            breadcrumb.set([
              {
                index: 0,
                title: 'Зареєстровані',
                to: { name: 'document-list-registered' },
              },
            ])
          },
        },
      })
    }

    const reviewList = ref<any[]>([])
    const myReview = ref<{ id: number; status: string } | null>(null)
    const hasActiveReviewers = ref(false)

    const getMyReview = () => {
      return documentReviewList(documentId.value).then(reviews => {
        reviewList.value = reviews
        const activeReviews = reviews.filter(r =>
          ['pending', 'in_review'].includes(r.status)
        )
        hasActiveReviewers.value = activeReviews.length > 0

        myReview.value =
          activeReviews.find(
            r => r.reviewerId === store.state.user.userData?.id
          ) ?? null
      })
    }

    const sendToReview = () => {
      openDialog({
        name: 'DocumentSendToReview',
        dialogData: {
          documentId: documentId.value,
        },
        params: {
          cb: async () => {
            await getDocument()
            await getMyReview()
            setSnackbar({ text: 'Документ передано на розгляд' })
          },
        },
      })
    }

    const returnFromReview = () => {
      if (myReview.value) {
        openDialog({
          name: 'DocumentReturnFromReview',
          dialogData: {
            reviewId: myReview.value.id,
          },
          params: {
            cb: async () => {
              await getDocument()
              await getMyReview()
              setSnackbar({ text: 'Документ повернуто з розгляду' })
            },
          },
        })
      }
    }

    const changeReviewer = (afterSuccess?: () => void) => {
      if (myReview.value?.id) {
        openDialog({
          name: 'DocumentChangeReviewer',
          dialogData: {
            reviewId: myReview.value.id,
          },
          params: {
            cb: async () => {
              await getDocument()
              await getMyReview()
              setSnackbar({ text: 'Наглядаючого змінено' })
              afterSuccess?.()
            },
          },
        })
      }
    }

    const rejectRegistration = () => {
      return handleAsync('rejectRegistration', async () => {
        const res = await documentRejectRegistration(documentId.value)
        setDocument(res)
        setSnackbar({ text: 'Документ не зареєстровано' })
      })
    }

    const returnFromRegistration = () => {
      return handleAsync('returnFromRegistration', async () => {
        const res = await documentReturnFromRegistration(documentId.value)
        setDocument(res)
        setSnackbar({ text: 'Документ повернуто на доопрацювання' })
      })
    }

    const deleteDocument = () => {
      confirm({
        text: `Ви впевнені, що хочете видалити документ "${document?.title}"?`,
      }).then(() => {
        return documentDelete(documentId.value).then(() => {
          router.push({ name: 'document-list-draft' })
        })
      })
    }

    const routeState = useDocumentRoute(documentContext)
    const flowState = useDocumentFlow(
      () => documentSource.docTypeClass?.flowType
    )
    const acceptanceState = useAcceptance(documentContext, {
      addDocumentSignatures: routeState.addDocumentSignatures,
    })
    const attachmentsState = useAttachments(documentContext)
    const relationState = useRelation(documentContext)
    const versionsState = useVersions(documentContext)
    const resolutionsState = useResolutions(documentContext)

    return {
      documentId,
      documentUuid,
      documentRequestError,
      documentSource,
      documentCredentials,
      getDocument,
      setDocument,
      disabled,
      updateDocument,
      updateDocumentFile,
      deleteDocument,
      revision,
      sendToRegistration,
      registerDocument,
      sendToReview,
      returnFromReview,
      myReview,
      hasActiveReviewers,
      reviewList,
      getMyReview,
      changeReviewer,
      rejectRegistration,
      returnFromRegistration,
      ...routeState,
      ...flowState,
      ...acceptanceState,
      ...attachmentsState,
      ...relationState,
      ...versionsState,
      ...resolutionsState,
    }
  })
